/**
 * Drawer Builder — design tokens (platform-neutral source of truth).
 *
 * The CSS custom properties in assets/drawer-builder.css are the *web view*
 * of this token set. This file exists so the same values (and, crucially,
 * the native-spring equivalents of the web easing curves) can be imported by
 * a future React-Native theme / Reanimated config without re-deriving them
 * from cubic-beziers.
 *
 * Values are Forage-informed (see the ios-forage teardown). Keep this file
 * and the `:root { --db-* }` block in drawer-builder.css in lockstep; if you
 * change a value in one, change it in the other.
 *
 * Loaded as a classic deferred script (like drawer-builder-constants.js).
 * Consumers MUST keep a literal fallback at the call site so a load-order
 * miss can never break motion (e.g.
 * `(window.MD_TOKENS && window.MD_TOKENS.motion.durBaseMs) || 300`).
 */
(function () {
  'use strict';

  window.MD_TOKENS = window.MD_TOKENS || {
    motion: {
      // Durations (ms) — mirror --db-dur-* in CSS.
      durFastMs: 150,      // tap / press feedback        (Forage 0.15s)
      durBaseMs: 300,      // cross-dissolve / page move   (Forage 0.30s)
      durSheetMs: 320,     // bottom-sheet snap            (== ANIM_MS)
      durProgressMs: 400,  // funnel progress fill         (Forage slider 0.40s)

      // Web easing curves — mirror --db-ease-* in CSS.
      easeOut: 'cubic-bezier(0.32, 0.72, 0, 1)',   // iOS decel tail
      easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',

      // Native-spring equivalents (React-Native Reanimated `withSpring`).
      // Forage uses Hero `.spring(stiffness: 250, damping: 15)` for its
      // hero/transition motion; use this directly on native instead of
      // approximating the bezier. `mass` defaults to 1 in Reanimated.
      spring: { stiffness: 250, damping: 15, mass: 1 },

      // Press-down scale (Forage tap 0.97) — mirror --db-press-scale.
      pressScale: 0.97,
    },

    // Elevation — mirror --db-shadow-* in CSS. Forage shadows are soft and
    // bottom-biased (radius 3 / black @10% / offset (0,2)).
    elevation: {
      // Web (CSS box-shadow strings).
      card: '0 2px 3px rgba(0, 0, 0, 0.10)',
      pop: '0 8px 24px rgba(0, 0, 0, 0.12)',
      sheet: '0 -8px 24px rgba(0, 0, 0, 0.10)',
      // Native-friendly decomposition (RN shadow* / elevation).
      cardNative: { color: '#000', opacity: 0.10, radius: 3, offset: { width: 0, height: 2 } },
    },

    // Corner radii (px) — mirror --db-radius-* in CSS.
    radius: {
      sm: 4,
      md: 6,
      lg: 8,
      card: 12,   // softened, Forage-informed
      sheet: 16,  // softened, Forage-informed
      full: 999,
    },

    // Spacing scale (px) — mirror --db-space-* in CSS.
    space: { xs2: 4, xs: 6, sm: 8, md: 12, lg: 16, xl: 20, xl2: 24 },

    color: {
      primary: '#3F3FF8',
      primaryHover: '#2828c4',
      primaryContrast: '#ffffff',
      muted: '#ababaf', // inactive / muted label (Forage progression grey)
    },
  };
})();
